// src/components/Shell.jsx
import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useStore } from '../store/useStore';
import AgentChat from './Agent/AgentChat';
import {
  LayoutDashboard, FileText, Bell, Globe, Bot, Settings,
  Cloud, Database, Leaf, Server, Zap, Menu, Shield,
  Building2, FolderKanban, AppWindow, Users, ShieldCheck, Cpu, Network,
  PlugZap, BrainCircuit, Layers, Activity, Sun, Moon, Microscope, Sparkles, ListChecks,
} from 'lucide-react';

// ─── ProbeStack logo SVG ───────────────────────────────────────────────────────
function ProbeStackIcon({ size = 30 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="ps-full" x1="44" y1="0" x2="0" y2="44" gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="#f97316" />
          <stop offset="48%"  stopColor="#eab308" />
          <stop offset="100%" stopColor="#14b8a6" />
        </linearGradient>
        <linearGradient id="ps-orange" x1="0" y1="0" x2="0" y2="1">
          <stop stopColor="#fb923c" />
          <stop offset="1" stopColor="#f97316" />
        </linearGradient>
        <linearGradient id="ps-teal" x1="0" y1="0" x2="0" y2="1">
          <stop stopColor="#2dd4bf" />
          <stop offset="1" stopColor="#0d9488" />
        </linearGradient>
      </defs>
      {/* Hexagon body — pointy-top */}
      <path
        d="M22 2L37.5 11.5V32.5L22 42L6.5 32.5V11.5Z"
        fill="url(#ps-full)" fillOpacity="0.13"
        stroke="url(#ps-full)" strokeWidth="1.6"
      />
      {/* P — left side, orange */}
      <line x1="13" y1="14" x2="13" y2="30" stroke="url(#ps-orange)" strokeWidth="2.6" strokeLinecap="round"/>
      <path
        d="M13 14 H18.5 C20.98 14 23 16.02 23 18.5 C23 20.98 20.98 23 18.5 23 H13"
        stroke="url(#ps-orange)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" fill="none"
      />
      {/* S — right side, teal */}
      <path
        d="M30.5 17 C30.5 17 29 14.5 26.5 14.5 C24 14.5 22.5 16 23.5 18 C24.5 20 27.5 20.5 28.5 22.5 C29.5 24.5 28 26.5 25.5 26.5 C23 26.5 21.5 25 21.5 25"
        stroke="url(#ps-teal)" strokeWidth="2.6" strokeLinecap="round" fill="none"
      />
    </svg>
  );
}

// ─── KRE Observability wordmark ────────────────────────────────────────────────
function KREWordmark({ size = 15 }) {
  return (
    <svg height={size} viewBox="0 0 180 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="ps-text-grad" x1="0" y1="0" x2="180" y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="#f97316" />
          <stop offset="42%"  stopColor="#eab308" />
          <stop offset="100%" stopColor="#14b8a6" />
        </linearGradient>
      </defs>
      <text
        x="0" y="14"
        fontFamily="Inter, sans-serif"
        fontWeight="700"
        fontSize="15"
        letterSpacing="-0.3"
        fill="url(#ps-text-grad)"
      >KRE Observability</text>
    </svg>
  );
}

// ─── NAV config ───────────────────────────────────────────────────────────────
const NAV = [
  { label: 'Overview',     icon: LayoutDashboard, to: '/overview',  badge: null },
  { label: 'Live Logs',    icon: FileText,         to: '/logs',      badge: '∞' },
  { label: 'Alerts',       icon: Bell,             to: '/alerts',    badge: 'alerts' },
  { label: 'Topology',     icon: Globe,            to: '/topology',  badge: null },
  { divider: 'Sources' },
  { label: 'Connectors',    icon: PlugZap,   to: '/connections',               badge: null },
  { label: 'GCP',           icon: Cloud,     to: '/sources/gcp',               badge: null, providerColor: '#4285f4' },
  { label: 'Azure Monitor', icon: Server,    to: '/sources/azure',             badge: null, providerColor: '#0078d4' },
  { label: 'MongoDB',       icon: Leaf,      to: '/sources/mongodb',           badge: null, providerColor: '#22c55e' },
  { label: 'PostgreSQL',    icon: Database,  to: '/sources/database',          badge: null, providerColor: '#a78bfa' },
  { label: 'AWS CloudWatch',icon: Cloud,     to: '/connections#cloudwatch',    badge: null, providerColor: '#f97316' },
  { label: 'Elasticsearch', icon: Globe,     to: '/connections#elasticsearch', badge: null, providerColor: '#f0c000' },
  { label: 'Prometheus',    icon: Zap,       to: '/connections#prometheus',    badge: null, providerColor: '#e6522c' },
  { label: 'Datadog',       icon: Activity,  to: '/connections#datadog',       badge: null, providerColor: '#632ca6' },
  { label: 'Splunk',        icon: Shield,    to: '/connections#splunk',        badge: null, providerColor: '#ef4444' },
  { label: 'Kafka',         icon: Zap,       to: '/connections#kafka',         badge: null, providerColor: '#b45309' },
  { label: 'Redis',         icon: Database,  to: '/connections#redis',         badge: null, providerColor: '#dc2626' },
  { label: 'Grafana Loki',  icon: FileText,  to: '/connections#loki',          badge: null, providerColor: '#f27405' },
  { divider: 'Management' },
  { label: 'Organizations', icon: Building2,   to: '/orgs',       badge: null },
  { label: 'Projects',      icon: FolderKanban,to: '/projects',   badge: null },
  { label: 'Applications',  icon: AppWindow,   to: '/apps',       badge: null },
  { label: 'Developers',    icon: Users,       to: '/developers', badge: null },
  { label: 'RBAC',          icon: ShieldCheck, to: '/rbac',       badge: null },
  { divider: 'Security' },
  { label: 'Security & Compliance', icon: Shield, to: '/security', badge: null },
  { divider: 'AI Providers' },
  { label: 'Connectors',    icon: PlugZap,      to: '/agent-connectors',           badge: null },
  { label: 'Bedrock',       icon: Cloud,        to: '/agent-connectors#bedrock',   badge: null, providerColor: '#f97316' },
  { label: 'Azure AI',      icon: Server,       to: '/agent-connectors#azureai',   badge: null, providerColor: '#0078d4' },
  { label: 'Vertex AI',     icon: Globe,        to: '/agent-connectors#vertexai',  badge: null, providerColor: '#4285f4' },
  { label: 'OpenAI',        icon: Bot,          to: '/agent-connectors#openai',    badge: null, providerColor: '#10a37f' },
  { label: 'Anthropic',     icon: BrainCircuit, to: '/agent-connectors#anthropic', badge: null, providerColor: '#d97706' },
  { label: 'More Providers',icon: Layers,       to: '/agent-connectors#more',      badge: null },
  { divider: 'Data Platform' },
  { label: 'Intelligence Hub',   icon: Sparkles, to: '/intel-hub', badge: null },
  { divider: 'Intelligence' },
  { label: 'Agent Inventory',    icon: ListChecks, to: '/agent-inventory', badge: '8' },
  { label: 'AI Agent',           icon: Bot,     to: '/agent',     badge: '!' },
  { label: 'Agent Observability',icon: Cpu,     to: '/agent-obs', badge: null },
  { label: 'AgentCore',          icon: Network,     to: '/agent-core', badge: null },
  { label: 'Root Cause Analysis',icon: Microscope,  to: '/rca',        badge: '3' },
];

export default function Shell({ children }) {
  const { alerts, sources, agentStatus, sidebarOpen, toggleSidebar } = useStore();
  const [clock,    setClock]    = useState('');
  const [chatOpen, setChatOpen] = useState(false);
  const [isDark,   setIsDark]   = useState(() => {
    const saved = localStorage.getItem('kre_obs_theme');
    return saved !== 'light';
  });

  // Apply theme to <html>
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    localStorage.setItem('kre_obs_theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  // Clock tick
  useEffect(() => {
    const tick = () => setClock(new Date().toUTCString().slice(17, 25) + ' UTC');
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const openAlerts  = alerts.filter(a => a.status === 'OPEN').length;
  const criticalCount = alerts.filter(a => a.severity === 'CRITICAL' && a.status === 'OPEN').length;

  function getBadge(item) {
    if (item.badge === 'alerts') return openAlerts > 0 ? openAlerts : null;
    if (item.badge === '!')      return agentStatus === 'thinking' || agentStatus === 'tool-call' ? '•' : null;
    return item.badge;
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden">

      {/* ── Topbar ──────────────────────────────────────────────────────────── */}
      <header
        className="flex items-center gap-3 px-4 h-14 shrink-0 z-50"
        style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)', boxShadow: 'var(--topbar-shadow)' }}>

        {/* Hamburger */}
        <button onClick={toggleSidebar}
          className="flex items-center justify-center w-8 h-8 rounded-lg transition-colors"
          style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer' }}>
          <Menu size={18} />
        </button>

        {/* KRE Observability branding */}
        <div className="flex items-center gap-2.5">
          <ProbeStackIcon size={30} />
          <KREWordmark size={15} />
        </div>

        <div style={{ width: 1, height: 20, background: 'var(--border)', flexShrink: 0 }} className="mx-1" />

        {/* Source status pills */}
        <div className="flex gap-1.5 items-center">
          {[
            { label: 'GCP',     type: 'GCP'      },
            { label: 'Azure',   type: 'AZURE'    },
            { label: 'MongoDB', type: 'MONGODB'  },
            { label: 'DB',      type: 'DATABASE' },
          ].map(s => {
            const src    = sources.find(x => x.type === s.type);
            const status = src?.status || 'DISCONNECTED';
            const color  = status === 'CONNECTED' ? '#22c55e' : status === 'DEGRADED' ? '#f59e0b' : '#ef4444';
            const suffix = status === 'CONNECTED' ? '' : status === 'DEGRADED' ? ' !' : ' ✕';
            return (
              <div key={s.label}
                className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                style={{ border: `1px solid ${color}40`, background: `${color}14`, color }}
                title={`${s.label}: ${status}${src?.lastSeenAt ? ` · last seen ${new Date(src.lastSeenAt).toLocaleTimeString()}` : ''}`}>
                <span className="pulse-dot" style={{ color }} />
                {s.label}{suffix}
              </div>
            );
          })}
        </div>

        {/* Right side */}
        <div className="ml-auto flex items-center gap-2">
          {criticalCount > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 rounded text-xs font-bold"
              style={{ background: 'rgba(239,68,68,.15)', color: '#f87171', border: '1px solid rgba(239,68,68,.3)' }}>
              🚨 {criticalCount} critical
            </div>
          )}

          {/* Clock */}
          <div className="mono text-xs px-2 py-1 rounded"
            style={{ color: 'var(--text-3)', border: '1px solid var(--border)', background: 'var(--bg-tertiary)' }}>
            {clock}
          </div>

          {/* Theme toggle */}
          <button
            onClick={() => setIsDark(d => !d)}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            className="flex items-center justify-center w-8 h-8 rounded-lg transition-all"
            style={{
              background: isDark ? 'rgba(251,191,36,.1)' : 'rgba(59,130,246,.1)',
              border: isDark ? '1px solid rgba(251,191,36,.25)' : '1px solid rgba(59,130,246,.25)',
              color: isDark ? '#fbbf24' : '#3b82f6',
              cursor: 'pointer',
              flexShrink: 0,
            }}>
            {isDark ? <Sun size={14} /> : <Moon size={14} />}
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">

        {/* ── Sidebar ──────────────────────────────────────────────────────── */}
        <nav style={{
          width: sidebarOpen ? 220 : 0,
          background: 'var(--bg-secondary)',
          borderRight: '1px solid var(--border)',
          overflow: 'hidden',
          transition: 'width .2s ease',
          flexShrink: 0,
        }}>
          <div style={{ width: 220, overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none' }}
            className="flex flex-col h-full py-3">

            {NAV.map((item, i) => {
              if (item.divider) return (
                <div key={i} className="px-3 mt-3 mb-1"
                  style={{ fontSize: 10, letterSpacing: '1.5px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-3)' }}>
                  {item.divider}
                </div>
              );

              const Icon        = item.icon;
              const badge       = getBadge(item);
              const accentColor = item.providerColor || '#f97316';

              return (
                <NavLink key={item.to} to={item.to.split('#')[0]}
                  className="nav-item"
                  style={({ isActive }) => ({
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: item.providerColor ? '6px 16px 6px 28px' : '8px 16px',
                    fontSize: item.providerColor ? 12 : 13,
                    color: isActive ? accentColor : item.providerColor ? `${item.providerColor}99` : 'var(--text-2)',
                    background: isActive ? `${accentColor}15` : 'transparent',
                    borderLeft: isActive ? `2px solid ${accentColor}` : '2px solid transparent',
                    textDecoration: 'none', transition: 'all .15s',
                  })}>
                  <Icon size={item.providerColor ? 13 : 15} style={{ flexShrink: 0, color: item.providerColor || 'inherit' }} />
                  <span style={{ flex: 1, whiteSpace: 'nowrap' }}>{item.label}</span>
                  {badge && (
                    <span className="text-xs font-bold rounded-full flex items-center justify-center min-w-[18px] h-[18px] px-1"
                      style={{
                        background: badge === '!' || badge === '•' ? 'rgba(245,158,11,.2)' : 'rgba(239,68,68,.2)',
                        color:      badge === '!' || badge === '•' ? '#fbbf24' : '#f87171',
                      }}>
                      {badge}
                    </span>
                  )}
                </NavLink>
              );
            })}

            <div className="mt-auto">
              <NavLink to="/settings" className="nav-item"
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', fontSize: 13, color: 'var(--text-2)', textDecoration: 'none', borderLeft: '2px solid transparent' }}>
                <Settings size={15} /> Settings
              </NavLink>
            </div>
          </div>
        </nav>

        {/* ── Main content ─────────────────────────────────────────────────── */}
        <main style={{ background: 'var(--bg-primary)', flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {children}
        </main>
      </div>

      {/* ── AI Chat FAB ──────────────────────────────────────────────────────── */}
      {!chatOpen && (
        <button onClick={() => setChatOpen(true)}
          className="fixed bottom-5 right-5 w-12 h-12 rounded-full flex items-center justify-center text-lg z-50 transition-transform hover:scale-110"
          style={{ background: 'linear-gradient(135deg,#f97316,#14b8a6)', boxShadow: '0 8px 24px rgba(249,115,22,.45)', border: 'none', cursor: 'pointer' }}>
          🤖
        </button>
      )}
      {chatOpen && <AgentChat onClose={() => setChatOpen(false)} />}
    </div>
  );
}
